import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { toast } from "react-toastify";
import { useState, useEffect, useMemo } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css/pagination";
import axios from "axios";

import Accordion from "../Accordion";
import ThicknessSelector from "../ThicknessSelector";
import CareInstructions from "./CareInstructions";

const API_URL = "https://admin.bodiosyakdown.com/api";
const UPLOADS_URL = "https://bodiosyakdown.com/uploads";

// Swiper custom styles
const swiperStyles = `
  .product-swiper {
    width: 100% !important;
    height: 100% !important;
  }
  .product-swiper .swiper-wrapper {
    height: 100% !important;
  }
  .product-swiper .swiper-slide {
    width: 100% !important;
    height: 100% !important;
  }
  .product-swiper .swiper-slide img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
  .product-swiper .swiper-button-prev,
  .product-swiper .swiper-button-next {
    color: #000;
    background: rgba(255,255,255,0.9);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.2s ease;
  }
  .product-swiper .swiper-button-prev:hover,
  .product-swiper .swiper-button-next:hover {
    background: #fff;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  .product-swiper .swiper-button-prev::after,
  .product-swiper .swiper-button-next::after {
    font-size: 18px;
    font-weight: bold;
  }
  .product-swiper .swiper-pagination-bullet {
    background: #000;
    opacity: 0.3;
  }
  .product-swiper .swiper-pagination-bullet-active {
    opacity: 1;
  }
  .thumb-swiper .swiper-slide {
    opacity: 0.5;
    cursor: pointer;
    transition: opacity 0.2s ease;
  }
  .thumb-swiper .swiper-slide-thumb-active {
    opacity: 1;
  }
`;

export function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);

  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [mainSwiper, setMainSwiper] = useState(null);

  const [defaultImages, setDefaultImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);

  // ==================== HELPERS ====================

  const getImageUrl = (filename) => {
    if (!filename) return "";
    // If already a full URL, return as is
    if (filename.startsWith('http')) {
      return filename;
    }
    return encodeURI(`${UPLOADS_URL}/${filename}`);
  };

  const safeParseJson = (str, fallback = []) => {
    if (Array.isArray(str)) return str;
    try {
      return JSON.parse(str || "[]");
    } catch {
      return fallback;
    }
  };

  // Extract filename from full URL or return as is
  const extractFilename = (imageValue) => {
    if (!imageValue) return null;
    if (imageValue.startsWith('http')) {
      return imageValue.split('/').pop();
    }
    return imageValue;
  };

  const findCPImage = (prod, color, size) => {
    if (!prod?.channelPrice?.length) return null;

    const c = (color || "").trim();
    const s = (size || "").trim();

    if (c && s) {
      const match = prod.channelPrice.find(
        (r) =>
          (r?.color || "").trim() === c &&
          (r?.size || "").trim() === s &&
          r?.image
      );
      if (match?.image) return extractFilename(match.image);
    }

    if (c) {
      const match = prod.channelPrice.find(
        (r) => (r?.color || "").trim() === c && r?.image
      );
      if (match?.image) return extractFilename(match.image);
    }

    return null;
  };

  const getColors = (prod) => {
    if (!prod?.channelPrice?.length) return [];
    return [
      ...new Set(
        prod.channelPrice
          .map((r) => (r?.color || "").trim())
          .filter(Boolean)
      ),
    ];
  };

  const getAllSizes = (prod) => {
    if (!prod) return [];

    const direct = safeParseJson(prod.size, []);
    const fromCP =
      prod.channelPrice?.map((r) => (r?.size || "").trim()).filter(Boolean) ||
      [];

    return [...new Set([...direct, ...fromCP])];
  };

  const getSizesForColor = (prod, color) => {
    if (!prod || !color) return getAllSizes(prod);

    const sizes = prod.channelPrice
      ?.filter((r) => (r?.color || "").trim() === color)
      .map((r) => (r?.size || "").trim())
      .filter(Boolean);

    return sizes?.length ? [...new Set(sizes)] : getAllSizes(prod);
  };

  const isSizeAvailable = (prod, color, size) => {
    if (!prod || !size) return false;
    if (!color) return true;
    return getSizesForColor(prod, color).includes(size);
  };

  const getStock = (prod, color, size) => {
    if (!prod) return 0;

    const cp = prod.channelPrice || [];
    const c = (color || "").trim().toLowerCase();
    const s = (size || "").trim().toLowerCase();

    if (c && s) {
      const match = cp.find(
        (r) =>
          (r?.color || "").trim().toLowerCase() === c &&
          (r?.size || "").trim().toLowerCase() === s
      );
      return match ? Number(match.stock) || 0 : 0;
    }

    if (c) {
      return cp
        .filter((r) => (r?.color || "").trim().toLowerCase() === c)
        .reduce((sum, r) => sum + (Number(r.stock) || 0), 0);
    }

    return Number(prod.stock) || 0;
  };

  const getPrice = (prod, color, size) => {
    if (!prod) return 0;

    const basePrice = Number(prod.price) || 0;
    if (!color || !size) return basePrice;

    const c = color.trim().toLowerCase();
    const s = size.trim().toLowerCase();

    const match = prod.channelPrice?.find(
      (r) =>
        (r?.color || "").trim().toLowerCase() === c &&
        (r?.size || "").trim().toLowerCase() === s
    );

    if (match?.pairPrice != null) {
      const pp = Number(match.pairPrice);
      if (Number.isFinite(pp)) return pp;
    }

    return basePrice;
  };

  const colorClassMap = {
    Black: "bg-black",
    White: "bg-white border-gray-300",
    "White yak": "bg-gray-100",
    "Brown yak": "bg-yellow-800",
    "Silver yak": "bg-gray-400",
    Beige: "bg-[#F5F5DC]",
    Blue: "bg-blue-500",
    Red: "bg-red-500",
    Green: "bg-green-500",
    Yellow: "bg-yellow-400",
    Gray: "bg-gray-400",
    Orange: "bg-orange-400",
    Purple: "bg-purple-400",
    Brown: "bg-yellow-800",
    Pink: "bg-pink-300",
    "Navy Blue": "bg-blue-900",
    Teal: "bg-teal-400",
    Maroon: "bg-red-900",
    "Olive Green": "bg-green-700",
    "Sky Blue": "bg-sky-300",
    "Dark brown": "bg-[#654321]",
    "Dark brown mixed": "bg-[#5C4033]",
    "Light brown": "bg-[#A67B5B]",
    "Light gray": "bg-gray-200",
    "Light grey": "bg-gray-200",
    "Cloudy grey": "bg-[#B8B8B8]",
    "Cloudy grey mixed": "bg-[#9E9E9E]",
    "black mixed": "bg-[#36454F]",
    Lavender: "bg-purple-200",
  };

  const getColorClass = (color) => colorClassMap[color] || "bg-gray-300";

  // ==================== DATA FETCHING ====================

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get(`${API_URL}/products/${productId}`, {
        headers,
      });
      const prod = res.data;

      prod.color = safeParseJson(prod.color, []);
      prod.size = safeParseJson(prod.size, []);
      prod.channelPrice =
        prod.channelPrice || safeParseJson(prod.channel_price, []);

      setProduct(prod);

      // Main images from images field
      const mainImgs = safeParseJson(prod.images, []);
      
      // Channel price images (extract filename from full URL)
      const cpImages = (prod.channelPrice || [])
        .map(cp => cp.image)
        .filter(Boolean)
        .map(img => extractFilename(img));
      
      // Combine all images (unique)
      const allImages = [...new Set([...mainImgs, ...cpImages])];
      
      // Order: mainImage first, then others
      const ordered = prod.mainImage
        ? [prod.mainImage, ...allImages.filter((img) => img !== prod.mainImage)]
        : allImages;

      setDefaultImages(ordered);
      setDisplayedImages(ordered);
    } catch (e) {
      console.error("Failed to fetch product:", e);
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  // ==================== IMAGE MANAGEMENT ====================

  const updateDisplayedImages = (color, size) => {
    if (!product) return;

    if (!color) {
      setDisplayedImages(defaultImages);
      mainSwiper?.slideTo(0);
      return;
    }

    const cpImage = findCPImage(product, color, size);

    if (cpImage) {
      const merged = [cpImage, ...defaultImages.filter((x) => x !== cpImage)];
      setDisplayedImages(merged);
      mainSwiper?.slideTo(0);
    } else {
      setDisplayedImages(defaultImages);
      mainSwiper?.slideTo(0);
    }
  };

  // ==================== EVENT HANDLERS ====================

  const handleSelectColor = (c) => {
    setSelectedColor(c);

    const available = getSizesForColor(product, c);

    if (available.length && (!selectedSize || !available.includes(selectedSize))) {
      setSelectedSize(available[0]);
      updateDisplayedImages(c, available[0]);
    } else {
      updateDisplayedImages(c, selectedSize);
    }
  };

  const handleSelectSize = (size) => {
    if (selectedColor && !isSizeAvailable(product, selectedColor, size)) {
      toast.warning(`Size ${size} is not available for ${selectedColor}`);
      return;
    }
    setSelectedSize(size);
  };

  useEffect(() => {
    if (!product || !selectedColor) return;
    updateDisplayedImages(selectedColor, selectedSize);
  }, [selectedSize]);

  // ==================== COMPUTED VALUES ====================

  const colors = useMemo(() => getColors(product), [product]);
  const allSizes = useMemo(() => getAllSizes(product), [product]);
  const availableSizes = useMemo(
    () => getSizesForColor(product, selectedColor),
    [product, selectedColor]
  );
  const currentStock = useMemo(
    () => getStock(product, selectedColor, selectedSize),
    [product, selectedColor, selectedSize]
  );
  const currentPrice = useMemo(
    () => getPrice(product, selectedColor, selectedSize),
    [product, selectedColor, selectedSize]
  );

  // ==================== CART ====================

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast.error("Please select a size.");
      return;
    }

    if (colors.length > 0 && !selectedColor) {
      toast.error("Please select a color.");
      return;
    }

    if (currentStock === 0) {
      toast.error("This option is out of stock.");
      return;
    }

    const currentImage = displayedImages[0] || product.mainImage;

    addToCart({
      id: product.id,
      title: product.name,
      price: currentPrice,
      image: currentImage ? getImageUrl(currentImage) : "",
      quantity: 1,
      color: selectedColor || null,
      size: selectedSize || null,
    });

    toast.success("🛒 Added to cart!");
  };

  // ==================== RENDER ====================

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">Product not found</p>
          <button
            onClick={() => navigate(-1)}
            className="text-black underline hover:no-underline"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{swiperStyles}</style>
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6">
        {/* Back button - mobile */}
        <button
          onClick={() => navigate(-1)}
          className="md:hidden flex items-center gap-2 text-sm text-gray-600 mb-4 hover:text-black transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-6 mt-24 md:mt-28">
          {/* Left - Images */}
          <div className="w-full lg:w-[calc(50%-12px)]">
            {/* Main Swiper */}
            <div className="overflow-hidden rounded-lg bg-gray-50 aspect-[3/4]">
              <Swiper
                modules={[Navigation, Thumbs, Pagination]}
                onSwiper={setMainSwiper}
                navigation
                pagination={{ clickable: true }}
                thumbs={{
                  swiper:
                    thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
                }}
                className="product-swiper h-full w-full"
              >
                {displayedImages.map((img, i) => (
                  <SwiperSlide key={`${img}-${i}`}>
                    <img
                      src={getImageUrl(img)}
                      alt={`${product.name} ${i + 1}`}
                      loading={i === 0 ? "eager" : "lazy"}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Thumbnail Swiper */}
            {displayedImages.length > 1 && (
              <div className="mt-4 hidden md:block">
                <Swiper
                  onSwiper={setThumbsSwiper}
                  spaceBetween={12}
                  slidesPerView={5}
                  watchSlidesProgress
                  className="thumb-swiper"
                >
                  {displayedImages.map((img, i) => (
                    <SwiperSlide key={`thumb-${img}-${i}`}>
                      <div className="aspect-square overflow-hidden rounded-md bg-gray-50 cursor-pointer hover:opacity-80 transition-opacity">
                        <img
                          src={getImageUrl(img)}
                          alt={`Thumbnail ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
          </div>

          {/* Right - Details */}
          <div className="w-full lg:flex-1 lg:pl-8">
            {/* Back button - desktop */}
            <button
              onClick={() => navigate(-1)}
              className="hidden md:flex items-center gap-2 text-sm text-gray-500 mb-6 hover:text-black transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to shop
            </button>

            {/* Product Title */}
            <h1 className="text-xl md:text-2xl font-semibold tracking-tight mb-2 font-libreFranklin">
              {product.name}
            </h1>

            {/* Description */}
            {product.description && (
              <p className="text-gray-600 leading-relaxed mb-6 font-libreFranklin">
                {product.description}
              </p>
            )}

            {/* Price */}
            <div className="mb-6">
              <p className="text-xl md:text-2xl font-semibold font-libreFranklin">
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(currentPrice)}
              </p>
            </div>

            {/* Stock Status */}
            <div className="mb-6">
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-libreFranklin text-sm font-medium ${
                  currentStock === 0
                    ? "bg-red-50 text-red-600"
                    : currentStock < 5
                    ? "bg-orange-50 text-orange-600"
                    : "bg-green-50 text-green-600"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    currentStock === 0
                      ? "bg-red-500"
                      : currentStock < 5
                      ? "bg-orange-500"
                      : "bg-green-500"
                  }`}
                ></span>
                {currentStock > 0
                  ? `${currentStock} in stock${
                      selectedColor && selectedSize
                        ? ` • ${selectedColor} / ${selectedSize}`
                        : ""
                    }`
                  : "Out of stock"}
              </div>
            </div>

            {/* Color Selection */}
            {colors.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-900 mb-3 font-libreFranklin">
                  Color: <span className="font-normal text-gray-600">{selectedColor || "Select a color"}</span>
                </p>
                <div className="flex gap-3 flex-wrap">
                  {colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => handleSelectColor(c)}
                      className={`w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                        selectedColor === c
                          ? "ring-2 ring-offset-2 ring-black border-black"
                          : "border-gray-200 hover:border-gray-400"
                      } ${getColorClass(c)}`}
                      title={c}
                      aria-label={c}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            <div className="mb-8">
              <p className="text-sm font-medium text-gray-900 mb-3 font-libreFranklin">
                Size: <span className="font-normal text-gray-600">{selectedSize || "Select a size"}</span>
              </p>
              <div className="flex gap-2 flex-wrap">
                {allSizes.map((size) => {
                  const isAvailable = isSizeAvailable(product, selectedColor, size);
                  const isSelected = selectedSize === size;

                  return (
                    <button
                      key={size}
                      onClick={() => handleSelectSize(size)}
                      disabled={!isAvailable}
                      className={`min-w-[48px] px-4 py-2.5 text-sm font-medium border rounded-md transition-all duration-200 ${
                        isSelected
                          ? "bg-black text-white border-black"
                          : isAvailable
                          ? "bg-white text-gray-900 border-gray-300 hover:border-black font-libreFranklin"
                          : "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed line-through font-libreFranklin"
                      }`}
                      title={
                        !isAvailable && selectedColor
                          ? `Not available for ${selectedColor}`
                          : undefined
                      }
                    >
                      {size}
                    </button>
                  );
                })}
                {!allSizes.length && (
                  <span className="text-gray-400 text-sm">No sizes available</span>
                )}
              </div>
              {selectedColor && availableSizes.length > 0 && (
                <p className="text-xs text-gray-500 mt-2 font-libreFranklin">
                  Available for {selectedColor}: {availableSizes.join(", ")}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <button
                onClick={handleAddToCart}
                disabled={!selectedSize || currentStock === 0}
                className={`flex-1 py-4 px-8 rounded-md font-medium font-libreFranklin text-sm uppercase tracking-wider transition-all duration-200 ${
                  !selectedSize || currentStock === 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-black text-white hover:bg-gray-900 active:scale-[0.98]"
                }`}
              >
                {currentStock === 0 ? "Out of Stock" : "Add to Cart"}
              </button>

              <button
                onClick={() => navigate("/cart")}
                className="py-4 px-8 rounded-md font-medium text-sm uppercase tracking-wider border-2 border-black text-black hover:bg-black hover:text-white transition-all duration-200 font-libreFranklin"
              >
                View Cart
              </button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-6 border-t border-b border-gray-100 font-libreFranklin">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-xs text-gray-600 uppercase tracking-wider font-libreFranklin">Worldwide Shipping</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <span className="text-xs text-gray-600 uppercase tracking-wider font-libreFranklin">Yak Cashmere</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <span className="text-xs text-gray-600 uppercase tracking-wider font-libreFranklin">Fast Delivery</span>
              </div>
            </div>

            {/* Accordion */}
            <div className="mt-8 space-y-0 font-libreFranklin">
              <Accordion title="Thickness">
                <ThicknessSelector value={product.thickness} />
              </Accordion>

              <Accordion title="Material">
                <p className="text-gray-600">{product.material || "100% Premium Yak Down"}</p>
              </Accordion>
              <Accordion title="Yakdown care">
                <CareInstructions customInstructions={product.care_instructions} />
              </Accordion>
            </div>
          </div>
        </div>

        {/* Gallery */}
        {displayedImages.length > 1 && (
          <div className="mt-16 md:mt-24">
            <h2 className="text-xl font-semibold mb-6 font-libreFranklin">Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {displayedImages.map((img, i) => (
                <div
                  key={`gallery-${img}-${i}`}
                  className="overflow-hidden rounded-lg bg-gray-50"
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`${product.name} gallery ${i + 1}`}
                    loading="lazy"
                    className="w-full h-auto object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}